/**
 * KPI Value Cell Component
 *
 * Editable cell for inline KPI value entry in the scorecard table.
 * Click to edit, Enter to save, Escape to cancel, Tab to move to next cell.
 */

import { useState, useRef, useCallback } from 'react';
import { Loader2, Lock, Unlock, MessageSquare } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { ScorecardKpi, ScorecardPeriodValue, NumberFormat } from '../types';

interface KpiValueCellProps {
  kpi: ScorecardKpi;
  periodValue: ScorecardPeriodValue | null;
  periodStart: string;
  periodEnd: string;
  onSave: (kpiId: string, datetime: string, value: number, notes?: string) => Promise<void>;
  onClear?: (kpiId: string, periodStart: string, periodEnd: string) => Promise<void>;
  isLoading?: boolean;
  isUnlocked?: boolean;
  onUnlock?: () => void;
  onNavigate?: (direction: 'up' | 'down' | 'left' | 'right') => void;
}

export function formatDisplayValue(value: number | null | undefined, unit?: string | null, format?: NumberFormat): string {
  if (value === null || value === undefined) return '';

  let formatted: string;
  const fmt = format || 'auto';

  switch (fmt) {
    case 'number':
      formatted = value.toLocaleString('en-CA', { maximumFractionDigits: 4 });
      break;
    case 'integer':
      formatted = Math.round(value).toLocaleString('en-CA', { maximumFractionDigits: 0 });
      break;
    case 'decimal_1':
      formatted = value.toLocaleString('en-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      break;
    case 'decimal_2':
      formatted = value.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      break;
    case 'auto':
    default:
      if (Math.abs(value) >= 1_000_000) {
        formatted = `${parseFloat((value / 1_000_000).toFixed(1))}M`;
      } else if (Math.abs(value) >= 1_000) {
        formatted = `${parseFloat((value / 1_000).toFixed(1))}K`;
      } else if (Number.isInteger(value)) {
        formatted = value.toLocaleString();
      } else {
        formatted = parseFloat(value.toFixed(2)).toLocaleString('en-CA', { maximumFractionDigits: 2 });
      }
      break;
  }

  if (unit) {
    return unit === '%' ? `${formatted}%` : `${formatted} ${unit}`;
  }
  return formatted;
}

/**
 * Determines the cell status based on whether value exists and period is overdue
 */
function getCellStatus(
  periodValue: ScorecardPeriodValue | null,
  periodEnd: string
): 'on_track' | 'off_track' | 'missing_overdue' | 'missing_future' | 'empty' {
  const hasValue = periodValue && (periodValue.value !== null || periodValue.value_count > 0);

  if (hasValue) {
    if (periodValue?.on_track === true) return 'on_track';
    if (periodValue?.on_track === false) return 'off_track';
    return 'empty';
  }

  // No value - check if period has ended (overdue)
  const periodEndDate = new Date(periodEnd);
  const now = new Date();

  if (now > periodEndDate) {
    return 'missing_overdue';
  }

  return 'missing_future';
}

/**
 * Gets the appropriate CSS classes based on cell status
 */
function getCellStyles(status: ReturnType<typeof getCellStatus>): string {
  switch (status) {
    case 'on_track':
      return 'bg-green-50 text-green-700';
    case 'off_track':
      return 'bg-red-50 text-red-700';
    case 'missing_overdue':
      return 'bg-amber-50 border-amber-300 border-dashed';
    case 'missing_future':
      return 'bg-gray-50 text-gray-400';
    case 'empty':
    default:
      return '';
  }
}

export function KpiValueCell({
  kpi,
  periodValue,
  periodStart,
  periodEnd,
  onSave,
  onClear,
  isLoading = false,
  isUnlocked = false,
  onUnlock,
  onNavigate,
}: KpiValueCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Determine which value to display based on KPI settings
  const displayValue = periodValue
    ? kpi.show_average
      ? periodValue.value_avg
      : kpi.show_total
        ? periodValue.value_sum
        : periodValue.value
    : null;

  const cellStatus = getCellStatus(periodValue, periodEnd);
  const cellStyles = getCellStyles(cellStatus);

  // Check if period is editable
  // Past periods are locked by default but can be unlocked via toggle
  const periodEndDate = new Date(periodEnd);
  const now = new Date();
  const isPastPeriod = now > periodEndDate;
  const isLocked = isPastPeriod && !isUnlocked;

  const handleStartEdit = useCallback(() => {
    if (isLoading || isSaving || isLocked) return;
    setEditValue(displayValue?.toString() ?? '');
    setEditNotes(periodValue?.notes ?? '');
    setIsEditing(true);
  }, [isLoading, isSaving, displayValue, isLocked, periodValue?.notes]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
    setEditNotes('');
  }, []);

  const handleClear = useCallback(async () => {
    if (!onClear) return;
    setIsSaving(true);
    try {
      await onClear(kpi.id, periodStart, periodEnd);
      setIsEditing(false);
      setEditValue('');
      setEditNotes('');
    } catch {
      // parent surfaces toast
    } finally {
      setIsSaving(false);
    }
  }, [onClear, kpi.id, periodStart, periodEnd]);

  const handleSave = useCallback(async () => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      // Empty input on an existing value = clear it back to null.
      // Empty input on a never-set cell = just close the popover.
      if (periodValue && onClear) {
        await handleClear();
      } else {
        handleCancel();
      }
      return;
    }

    const numValue = parseFloat(trimmed);
    if (isNaN(numValue) || !isFinite(numValue)) {
      handleCancel();
      return;
    }

    setIsSaving(true);
    try {
      // Use the middle of the period as the datetime for the value
      const periodStartDate = new Date(periodStart);
      const periodEndDate = new Date(periodEnd);
      const midpoint = new Date((periodStartDate.getTime() + periodEndDate.getTime()) / 2);

      const notesValue = editNotes.trim() || undefined;
      await onSave(kpi.id, midpoint.toISOString(), numValue, notesValue);
      setIsEditing(false);
      setEditValue('');
      setEditNotes('');
    } catch {
      // Error handled by parent via toast
    } finally {
      setIsSaving(false);
    }
  }, [editValue, editNotes, kpi.id, periodStart, periodEnd, onSave, handleCancel]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          handleSave();
          break;
        case 'Escape':
          e.preventDefault();
          handleCancel();
          break;
        case 'Tab':
          e.preventDefault();
          handleSave();
          // Navigate to next cell
          onNavigate?.(e.shiftKey ? 'left' : 'right');
          break;
        case 'ArrowUp':
          if (!e.shiftKey) {
            e.preventDefault();
            handleSave();
            onNavigate?.('up');
          }
          break;
        case 'ArrowDown':
          if (!e.shiftKey) {
            e.preventDefault();
            handleSave();
            onNavigate?.('down');
          }
          break;
      }
    },
    [handleSave, handleCancel, onNavigate]
  );

  // Show loading state
  if (isLoading || isSaving) {
    return (
      <div
        className={cn(
          'flex items-center justify-center h-10 min-w-[80px] px-2 rounded',
          cellStyles
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Locked past period with unlock option
  if (isLocked && onUnlock) {
    return (
      <div
        className={cn(
          'flex items-center justify-end h-10 min-w-[100px] px-2 rounded',
          'opacity-60',
          cellStyles
        )}
      >
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnlock();
                }}
                className="p-1 rounded hover:bg-muted/80 transition-colors cursor-pointer mr-1"
                aria-label={`Unlock ${kpi.name} for editing`}
              >
                <Lock className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Click to unlock for editing</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="text-sm font-medium whitespace-nowrap">
          {displayValue !== null ? (
            formatDisplayValue(displayValue, kpi.goal_unit, kpi.number_format)
          ) : (
            <span className="text-muted-foreground">
              {cellStatus === 'missing_overdue' ? '—' : ''}
            </span>
          )}
        </span>
        {periodValue?.notes && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <MessageSquare className="h-3 w-3 text-blue-400 ml-1 shrink-0" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">{periodValue.notes}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  // Editable cell with popover for editing
  return (
    <Popover open={isEditing} onOpenChange={(open) => {
      if (open) {
        handleStartEdit();
      } else if (editValue.trim()) {
        handleSave();
      } else if (periodValue && onClear) {
        // User erased an existing value and closed the popover → clear to null.
        handleClear();
      } else {
        handleCancel();
      }
    }}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'flex items-center justify-end h-10 min-w-[100px] px-2 rounded',
            'cursor-pointer hover:bg-muted/50 transition-colors',
            cellStyles,
            isPastPeriod && isUnlocked && 'ring-1 ring-amber-300/50'
          )}
          tabIndex={0}
          role="button"
          aria-label={`Edit ${kpi.name} value for period`}
        >
          {isPastPeriod && isUnlocked && (
            <Unlock className="h-3 w-3 text-amber-500 mr-1" />
          )}
          <span className="text-sm font-medium">
            {displayValue !== null ? (
              formatDisplayValue(displayValue, kpi.goal_unit, kpi.number_format)
            ) : (
              <span className="text-muted-foreground">
                {cellStatus === 'missing_overdue' ? '—' : ''}
              </span>
            )}
          </span>
          {periodValue?.notes && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <MessageSquare className="h-3 w-3 text-blue-400 ml-1 shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">{periodValue.notes}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        side="bottom"
        align="end"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          // Focus the input after popover opens
          setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
          }, 0);
        }}
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`kpi-value-${kpi.id}`} className="text-xs font-medium">
              Value {kpi.goal_unit ? `(${kpi.goal_unit})` : ''}
            </Label>
            <Input
              ref={inputRef}
              id={`kpi-value-${kpi.id}`}
              type="number"
              step="any"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-8 text-right text-sm"
              placeholder="Enter value..."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`kpi-notes-${kpi.id}`} className="text-xs font-medium">
              Notes
            </Label>
            <Textarea
              id={`kpi-notes-${kpi.id}`}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              className="min-h-[60px] text-sm resize-y"
              placeholder="Add a comment..."
              rows={2}
            />
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            {onClear && periodValue && (periodValue.value !== null || periodValue.value_count > 0) ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleClear}
              >
                Clear
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-7 text-xs"
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
